import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, AlertTriangle, Info, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnnouncementModal = ({ user }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (user && user.role === 'youth') {
      fetchPendingAnnouncements();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchPendingAnnouncements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements/pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setAnnouncements(data);
          setIsOpen(true);
        }
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const acknowledgeAnnouncement = async () => {
    const announcement = announcements[currentIndex];
    
    try {
      const res = await fetch(`${API_URL}/api/announcements/${announcement.id}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        if (currentIndex < announcements.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          setIsOpen(false);
          toast.success('All announcements acknowledged!');
        }
      }
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  if (!isOpen || announcements.length === 0) return null;

  const currentAnnouncement = announcements[currentIndex];
  const isUrgent = currentAnnouncement.category === 'urgent';
  const isReminder = currentAnnouncement.category === 'reminder';

  const getCategoryIcon = () => {
    if (isUrgent) return <AlertTriangle className="w-6 h-6 text-red-600" />;
    if (isReminder) return <Info className="w-6 h-6 text-yellow-600" />;
    return <Bell className="w-6 h-6 text-blue-600" />;
  };

  const getCategoryColor = () => {
    if (isUrgent) return 'bg-red-50 border-red-200';
    if (isReminder) return 'bg-yellow-50 border-yellow-200';
    return 'bg-blue-50 border-blue-200';
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg rounded-3xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
              isUrgent ? 'bg-red-100' : isReminder ? 'bg-yellow-100' : 'bg-blue-100'
            }`}>
              {getCategoryIcon()}
            </div>
            <div>
              <DialogTitle className="font-heading text-xl">
                Important Announcement
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                {currentIndex + 1} of {announcements.length} announcements
                <Badge className={
                  isUrgent ? 'bg-red-500' : isReminder ? 'bg-yellow-500' : 'bg-blue-500'
                }>
                  {currentAnnouncement.category}
                </Badge>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className={`p-5 rounded-2xl border-2 ${getCategoryColor()}`}>
          <h3 className="font-heading font-bold text-lg mb-3">
            {currentAnnouncement.title}
          </h3>
          <p className="text-muted-foreground whitespace-pre-wrap">
            {currentAnnouncement.content}
          </p>
        </div>

        <Button
          onClick={acknowledgeAnnouncement}
          className="w-full h-14 rounded-full text-lg"
          data-testid="acknowledge-announcement"
        >
          <CheckCircle className="w-5 h-5 mr-2" />
          I have read and understood
          {currentIndex < announcements.length - 1 && (
            <ArrowRight className="w-5 h-5 ml-2" />
          )}
        </Button>

        {/* Progress Dots */}
        {announcements.length > 1 && (
          <div className="flex justify-center gap-2 mt-2">
            {announcements.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-primary w-6' : 
                  idx < currentIndex ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementModal;
